import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Card, Label } from 'semantic-ui-react';
import { default as Icon } from '@plone/volto/components/theme/Icon/Icon';
import calendarSVG from '@plone/volto/icons/calendar.svg';
import arrowRightSVG from '@plone/volto/icons/right-key.svg';

const messages = defineMessages({
  repeatsEveryYear: {
    id: 'dateRangesWidget.repeatsEveryYear',
    defaultMessage: 'Repeats every year',
  },
  doesNotRepeat: {
    id: 'dateRangesWidget.doesNotRepeat',
    defaultMessage: 'Does not repeat annually',
  },
  missingDate: {
    id: 'dateRangesWidget.missingDate',
    defaultMessage: 'Date not set',
  },
});

const DateRangesWidget = ({ value }) => {
  const intl = useIntl();

  const datesRowStyle = React.useMemo(
    () => ({
      display: 'flex',
      alignItems: 'center',
      gap: '0.45rem',
      flexWrap: 'nowrap',
    }),
    [],
  );

  const dateBoxStyle = React.useMemo(
    () => ({
      background: '#e3e5ea',
      color: '#2f323a',
      borderRadius: '12px',
      padding: '0.35rem 0.55rem',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minWidth: '85px',
    }),
    [],
  );

  const dateLinesStyle = React.useMemo(
    () => ({
      display: 'flex',
      flexDirection: 'column',
      rowGap: '0.1rem',
      textAlign: 'center',
      fontWeight: 600,
      letterSpacing: '0.025em',
      lineHeight: 1.2,
      fontSize: '0.95rem',
    }),
    [],
  );

  const arrowStyle = React.useMemo(
    () => ({ flex: '0 0 auto', display: 'flex', alignItems: 'center' }),
    [],
  );

  const cardStyle = React.useMemo(
    () => ({
      minWidth: '280px',
      borderRadius: '12px',
      boxShadow: 'none',
      border: '1px solid #c8cbd3',
      overflow: 'hidden',
    }),
    [],
  );

  const cardContentStyle = React.useMemo(
    () => ({
      display: 'flex',
      gap: '0.75rem',
      alignItems: 'center',
      padding: '1.1rem',
      flexWrap: 'wrap',
    }),
    [],
  );

  const items = React.useMemo(() => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (Array.isArray(value.items)) return value.items;
    return [];
  }, [value]);

  const buildDateLines = React.useCallback(
    (date, { hideYear = false } = {}) => {
      if (!date) {
        return [intl.formatMessage(messages.missingDate)];
      }

      try {
        const options = hideYear
          ? {
              month: 'short',
              day: 'numeric',
            }
          : {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
            };

        if (intl.formatDateToParts) {
          const partMap = {};
          intl.formatDateToParts(date, options).forEach((part) => {
            if (['month', 'day', 'year'].includes(part.type)) {
              partMap[part.type] = part.value;
            }
          });

          const lines = [];
          if (partMap.day) lines.push(partMap.day);
          if (partMap.month) lines.push(partMap.month);
          if (!hideYear && partMap.year) lines.push(partMap.year);

          if (lines.length > 0) {
            return lines;
          }
        }

        const formatted = intl.formatDate(date, options);
        const tokens = formatted.split(/[\s,]+/).filter(Boolean);
        return tokens.length > 0 ? tokens : [formatted];
      } catch (e) {
        return [date];
      }
    },
    [intl],
  );

  if (items.length === 0) {
    return null;
  }

  return (
    <Card.Group
      stackable
      doubling
      itemsPerRow={3}
      className="date-ranges-widget"
      role="list"
    >
      {items.map((item, index) => {
        const { ['@id']: id, fromDate, toDate, repeatEveryYear } = item || {};
        const key = id || `${fromDate}-${toDate}-${index}`;

        const hideYear = repeatEveryYear === true;
        const hasRepeatFlag = typeof repeatEveryYear === 'boolean';
        const repeatLabel = repeatEveryYear
          ? intl.formatMessage(messages.repeatsEveryYear)
          : intl.formatMessage(messages.doesNotRepeat);

        const fromLines = buildDateLines(fromDate, { hideYear });
        const toLines = buildDateLines(toDate, { hideYear });

        return (
          <Card key={key} role="listitem" style={cardStyle}>
            <Card.Content style={cardContentStyle}>
              <Icon name={calendarSVG} size="24px" />
              <div
                className="date-ranges-widget__content"
                style={{ flex: 1, minWidth: 0 }}
              >
                <div
                  className="date-ranges-widget__dates"
                  style={datesRowStyle}
                >
                  <div className="date-ranges-widget__box" style={dateBoxStyle}>
                    <div style={dateLinesStyle}>
                      {fromLines.map((line, idx) => (
                        <span key={idx}>{line}</span>
                      ))}
                    </div>
                  </div>
                  <div className="date-ranges-widget__arrow" style={arrowStyle}>
                    <Icon name={arrowRightSVG} size="18px" />
                  </div>
                  <div className="date-ranges-widget__box" style={dateBoxStyle}>
                    <div style={dateLinesStyle}>
                      {toLines.map((line, idx) => (
                        <span key={idx}>{line}</span>
                      ))}
                    </div>
                  </div>
                </div>
                {hasRepeatFlag && (
                  <Label
                    className="date-ranges-widget__repeat"
                    style={{ marginTop: '0.75rem' }}
                    basic={!repeatEveryYear}
                    color={repeatEveryYear ? 'teal' : undefined}
                    content={repeatLabel}
                  />
                )}
              </div>
            </Card.Content>
          </Card>
        );
      })}
    </Card.Group>
  );
};

export default DateRangesWidget;
